import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { VehicleInformationModel } from '../models'
import { fetchVehicleError } from '../api'

import { TimeInMilliseconds } from 'src/Libs/types'
import { UserActionRequiredErrorDto } from '../dto'
import { usePortalQuery } from 'src/Libs/api'

interface UseVehicleInformation {
  vehicleError: UserActionRequiredErrorDto[] | null
  loading: boolean
  error?: Error | AuthFetchError
  hasError: boolean
}

const FETCH_INTERVAL = TimeInMilliseconds.oneHour
const STALE_TIME = TimeInMilliseconds.oneMinute
const MAX_RETRY_ATTEMPTS = 2
const MAX_RETRY_INTERVAL = TimeInMilliseconds.thirtySecond

/**
 * Hook for handling fetching of vehicle error that require user action
 */
export const useVehicleError = (
  installationId: number,
  vehicleId: VehicleInformationModel['id'],
  VehicleHasError: boolean
): UseVehicleInformation => {
  const queryKey = ['vehicle-integration', 'vehicle-error', vehicleId]
  const authFetch = useAuthFetchWithResponse()

  const queryFn = async (): Promise<UserActionRequiredErrorDto[] | null> => {
    const { error: fetchError, hasErrors, value } = await fetchVehicleError(installationId, vehicleId, authFetch)

    if (hasErrors || fetchError) {
      throw new Error(fetchError?.message)
    }
    return value
  }

  const { data, isLoading, isError } = usePortalQuery({
    queryKey,
    queryFn,
    /* enable query only if vehicle has error that require user action  */
    enabled: VehicleHasError,
    retry: MAX_RETRY_ATTEMPTS,
    /** retries after 0.5, 1 seconds */
    retryDelay: attempt => {
      return Math.min(500 * 2 ** attempt, MAX_RETRY_INTERVAL)
    },
    staleTime: STALE_TIME,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: FETCH_INTERVAL,
  })

  return { vehicleError: data ?? null, loading: isLoading, hasError: isError }
}
